import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Sparkles, Brain, FileText, ListTodo, FlaskConical, AlertCircle, Loader2 } from 'lucide-react';

const taskTypes = [
  { value: 'decode_draft', label: 'DECODE Draft', icon: Brain, description: 'Generate a comprehensive DECODE analysis draft' },
  { value: 'report_draft', label: '20-Day Report Draft', icon: FileText, description: 'Generate a progress report draft' },
  { value: 'protocol_draft', label: 'Protocol Draft', icon: ListTodo, description: 'Generate a protocol draft based on DECODE' },
  { value: 'lab_interpretation', label: 'Lab Interpretation', icon: FlaskConical, description: 'Interpret lab markers and identify patterns' }
];

export default function AdminAIWorkbench() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedTask, setSelectedTask] = useState('decode_draft');
  const [contextInput, setContextInput] = useState('');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API}/users?role=subscriber`);
        setUsers(response.data);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  const handleGenerate = async () => {
    if (!selectedUser) {
      toast.error('Please select a subscriber');
      return;
    }

    setLoading(true);
    setOutput('');

    try {
      let context = {};
      try {
        context = contextInput ? JSON.parse(contextInput) : {};
      } catch (e) {
        context = { additional_notes: contextInput };
      }

      const response = await axios.post(`${API}/ai/generate`, {
        task_type: selectedTask,
        subscriber_id: selectedUser,
        context: context
      });

      setOutput(response.data.output);
      toast.success('Generation complete - This is a DRAFT for review');
    } catch (error) {
      toast.error('AI generation failed');
      console.error('AI error:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedTaskInfo = taskTypes.find(t => t.value === selectedTask);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-['Outfit'] font-bold text-adm-primary text-adm-primary flex items-center gap-3" data-testid="ai-workbench-title">
            <Sparkles className="w-8 h-8 text-[#C89B7B]" />
            AI Workbench
          </h1>
          <p className="text-adm-tertiary text-adm-primary/60">Generate drafts for review (Admin Only)</p>
        </div>

        {/* Warning Banner */}
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-600 dark:text-amber-400">Important: AI outputs are drafts only</p>
              <p className="text-sm text-amber-600/80 dark:text-amber-400/80">
                All AI-generated content must be reviewed and edited by qualified staff before being shared with subscribers.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Input Panel */}
          <div className="space-y-4">
            <Card className="admin-card bg-adm-surface border-white/[0.06]">
              <CardHeader>
                <CardTitle className="text-adm-primary text-adm-primary font-['Outfit']">Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Task Type Selection */}
                <div className="grid grid-cols-2 gap-3">
                  {taskTypes.map(task => (
                    <button
                      key={task.value}
                      onClick={() => setSelectedTask(task.value)}
                      className={`p-4 rounded-lg border text-left transition-all ${
                        selectedTask === task.value
                          ? 'border-[#C89B7B] bg-[#C89B7B]/10'
                          : 'border-white/[0.06] border-white/[0.06] hover:border-[#C89B7B]/50'
                      }`}
                    >
                      <task.icon className={`w-5 h-5 mb-2 ${selectedTask === task.value ? 'text-[#C89B7B]' : 'text-adm-tertiary text-adm-primary/40'}`} />
                      <p className={`font-medium text-sm ${selectedTask === task.value ? 'text-[#C89B7B]' : 'text-slate-700 text-adm-primary'}`}>
                        {task.label}
                      </p>
                    </button>
                  ))}
                </div>

                {/* Subscriber Selection */}
                <div>
                  <label className="text-sm font-medium text-slate-700 text-adm-primary/80 mb-1.5 block">
                    Select Subscriber
                  </label>
                  <Select value={selectedUser} onValueChange={setSelectedUser}>
                    <SelectTrigger className="bg-adm-bg bg-adm-bg border-white/[0.06] border-white/[0.06]">
                      <SelectValue placeholder="Choose a subscriber" />
                    </SelectTrigger>
                    <SelectContent className="bg-adm-surface bg-adm-surface border-white/[0.06] border-white/[0.06] max-h-60">
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Context Input */}
                <div>
                  <label className="text-sm font-medium text-slate-700 text-adm-primary/80 mb-1.5 block">
                    Additional Context (Optional)
                  </label>
                  <Textarea
                    value={contextInput}
                    onChange={(e) => setContextInput(e.target.value)}
                    placeholder="Enter any additional context, notes, or JSON data for the AI to consider..."
                    className="bg-adm-bg bg-adm-bg border-white/[0.06] border-white/[0.06] min-h-[150px] font-mono text-sm"
                  />
                  <p className="text-xs text-adm-tertiary text-adm-primary/40 mt-1">
                    Can be plain text or JSON format for structured input
                  </p>
                </div>

                {/* Generate Button */}
                <Button
                  onClick={handleGenerate}
                  className="w-full copper-gradient text-[#0B0F19]"
                  disabled={loading || !selectedUser}
                  data-testid="generate-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate {selectedTaskInfo?.label}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Output Panel */}
          <Card className="admin-card bg-adm-surface border-white/[0.06]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-adm-primary text-adm-primary font-['Outfit']">Generated Output</CardTitle>
              {output && (
                <Badge className="bg-amber-500/20 text-amber-400">
                  DRAFT - Review Required
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader2 className="w-10 h-10 text-[#C89B7B] animate-spin mb-4" />
                  <p className="text-adm-tertiary text-adm-primary/50">Generating {selectedTaskInfo?.label}...</p>
                </div>
              ) : output ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-adm-bg bg-adm-bg max-h-[500px] overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm text-slate-700 text-adm-primary/80 font-mono">
                      {output}
                    </pre>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => navigator.clipboard.writeText(output)}
                      className="border-white/[0.06] border-white/[0.06]"
                    >
                      Copy to Clipboard
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setOutput('')}
                      className="border-white/[0.06] border-white/[0.06]"
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <Sparkles className="w-12 h-12 text-slate-200 text-adm-primary/20 mb-4" />
                  <p className="text-adm-tertiary text-adm-primary/50">
                    Select a task and subscriber, then click Generate
                  </p>
                  <p className="text-sm text-adm-tertiary text-adm-primary/30 mt-1">
                    Output will appear here for review
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
